/**
 * VÍDEO 3 — SINAL 1
 * "Se parar de postar, para de vender"
 * 10s | 720×900 | SEM ÁUDIO
 * Copy: BoardCopy (Hormozi + Sugarman)
 */

import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  interpolate,
  Easing,
  Sequence,
} from "remotion";
import {CLAMP} from "../../presets/idv";

const T = {
  dark:  { bg: "#1A1D26", headline: "#FFFFFF", body: "#C8C4C0", muted: "#888888" },
  accent: "#E84545",
  accentBg: "rgba(232,69,69,0.15)",
  term: { bg: "#0D1117", border: "rgba(255,255,255,0.08)", prompt: "#4ADE80", error: "#F87171", text: "#C9D1D9", comment: "#8B949E" },
};

const FONT_SERIF = "'Playfair Display', Georgia, serif";
const FONT_MONO = "'JetBrains Mono', 'Fira Code', monospace";
const FONT_SANS = "'Inter', system-ui, sans-serif";

const SZ = { top: 70, sides: 50, bottom: 80 };
const CW = 720 - SZ.sides * 2;

const fadeIn = (frame: number, start: number, end: number) =>
  interpolate(frame, [start, end], [0, 1], { extrapolateRight: CLAMP });
const slideUp = (frame: number, start: number, end: number, dist = 20) =>
  interpolate(frame, [start, end], [dist, 0], { extrapolateRight: CLAMP, easing: Easing.out(Easing.ease) });

const GridPattern: React.FC = () => {
  const frame = useCurrentFrame();
  const opacity = 0.03 + Math.sin(frame * 0.02) * 0.005;
  return (
    <div style={{
      position: "absolute", inset: 0,
      backgroundImage: `linear-gradient(rgba(255,255,255,${opacity}) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,${opacity}) 1px, transparent 1px)`,
      backgroundSize: "40px 40px", pointerEvents: "none",
    }} />
  );
};

const Headline: React.FC<{children: React.ReactNode; size?: number; color: string; delay?: number}> =
  ({children, size = 50, color, delay = 0}) => {
  const frame = useCurrentFrame();
  return (
    <div style={{
      opacity: fadeIn(frame, delay, delay + 15),
      transform: `translateY(${slideUp(frame, delay, delay + 15)})`,
      fontFamily: FONT_SERIF, fontSize: size, fontWeight: 700, lineHeight: 1.15, color, maxWidth: CW,
    }}>{children}</div>
  );
};

const Body: React.FC<{children: React.ReactNode; color: string; delay?: number}> = ({children, color, delay = 15}) => {
  const frame = useCurrentFrame();
  return (
    <div style={{
      opacity: fadeIn(frame, delay, delay + 15),
      transform: `translateY(${slideUp(frame, delay, delay + 15)})`,
      fontFamily: FONT_SERIF, fontSize: 22, fontWeight: 400, lineHeight: 1.4, color, maxWidth: CW,
    }}>{children}</div>
  );
};

const TerminalCard: React.FC<{
  label: string;
  lines: {type: "$" | "#" | "text" | "error" | "success"; content: string}[];
  delay?: number;
}> = ({label, lines, delay = 25}) => {
  const frame = useCurrentFrame();
  const opacity = fadeIn(frame, delay, delay + 15);
  const ty = slideUp(frame, delay, delay + 15, 40);
  return (
    <div style={{opacity, transform: `translateY(${ty})`, background: T.term.bg, border: `1px solid ${T.term.border}`, borderRadius: 12, padding: "0 0 20px 0", marginTop: 16, boxShadow: "0 8px 32px rgba(0,0,0,0.4)", maxWidth: CW}}>
      <div style={{display: "flex", alignItems: "center", padding: "10px 16px", borderBottom: `1px solid ${T.term.border}`, gap: 8}}>
        <div style={{width: 12, height: 12, borderRadius: "50%", background: "#FF5F57"}} />
        <div style={{width: 12, height: 12, borderRadius: "50%", background: "#FEBC2E"}} />
        <div style={{width: 12, height: 12, borderRadius: "50%", background: "#28C840"}} />
        <div style={{flex: 1}} />
        <span style={{fontFamily: FONT_MONO, fontSize: 12, color: T.term.comment}}>{label}</span>
      </div>
      <div style={{padding: "16px 20px", display: "flex", flexDirection: "column", gap: 8}}>
        {lines.map((line, i) => {
          const color = line.type === "$" ? T.term.prompt : line.type === "#" ? T.term.comment : line.type === "error" ? T.term.error : line.type === "success" ? T.term.prompt : T.term.text;
          const prefix = line.type === "$" ? "$ " : line.type === "#" ? "# " : "";
          const ld = delay + 15 + i * 8;
          const lo = fadeIn(frame, ld, ld + 6);
          return (
            <div key={i} style={{opacity: lo, fontFamily: FONT_MONO, fontSize: 15, lineHeight: 2, color}}>{prefix}{line.content}</div>
          );
        })}
        <div style={{opacity: Math.round(Math.sin(frame * 0.15) * 0.5 + 0.5), fontFamily: FONT_MONO, fontSize: 15, color: T.term.prompt}}>█</div>
      </div>
    </div>
  );
};

const Footer: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <div style={{position: "absolute", bottom: 40, left: SZ.sides, right: SZ.sides, display: "flex", justifyContent: "space-between", alignItems: "center", opacity: fadeIn(frame, 40, 50)}}>
      <span style={{fontFamily: FONT_SANS, fontSize: 15, fontWeight: 400, color: T.dark.muted}}>@guilhermeribeiro.ai</span>
      <span style={{fontFamily: FONT_SANS, fontSize: 13, fontWeight: 500, color: T.dark.muted}}>3/9</span>
    </div>
  );
};

const SeriesLabel: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <div style={{position: "absolute", top: 30, left: SZ.sides, opacity: fadeIn(frame, 0, 10), fontFamily: FONT_MONO, fontSize: 13, fontWeight: 500, letterSpacing: 2, textTransform: "uppercase", color: T.dark.muted}}>SISTEMA DE MARKETING</div>
  );
};

export const Video03Sinal1: React.FC = () => (
  <AbsoluteFill style={{backgroundColor: T.dark.bg}}>
    <Sequence from={0} durationInFrames={300}>
      <AbsoluteFill style={{justifyContent: "center", alignItems: "flex-start", padding: `${SZ.top}px ${SZ.sides}px ${SZ.bottom}px`}}>
        <GridPattern />
        <SeriesLabel />
        <Headline size={48} color={T.dark.headline} delay={5}>
          Se parar de postar,<br />para de vender
        </Headline>
        <Body color={T.dark.body} delay={15}>Dependência operacional total. Você é o gargalo.</Body>
        <TerminalCard
          label="diagnóstico"
          delay={30}
          lines={[
            {type: "$", content: "marketing_status --check"},
            {type: "#", content: "# resultado atual"},
            {type: "text", content: "posts_semana: variável"},
            {type: "text", content: "resultado: imprevisível"},
            {type: "error", content: "sistema: AUSENTE ✗"},
          ]}
        />
        <Footer />
      </AbsoluteFill>
    </Sequence>
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
